import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import config from '../../../config';
import { Form, Input, notification, Row, Col, Button, Modal, Upload, Spin, message, Select } from 'antd';
import * as userActions from '../../../redux/actions/userActions';
import { useDispatch } from 'react-redux';
import {
  confirmationModal,
  formItemLayout,
  handleBackendFormErrors,
  tailFormItemLayout,
  unexpectedError,
  beforeUpload,
} from '../../../common_functions';
import UploadTrigger from '../../../common_functions/UploadTrigger';

const PrintMount = props => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const isMounted = useRef(true);
  useEffect(() => () => (isMounted.current = false), []);
  const { section, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [imgUpload, setImgUpload] = useState({
    recto_texture: null,
    verso_texture: null,
    edge: null,
    mount_branding: null,
    folderID: null,
  });
  const [dropdownData, setDropdownData] = useState({
    plies: [],
    core: [],
    recto_facing_texture: [],
    verso_facing_texture: [],
    recto_facing_color: [],
    verso_facing_color: [],
    apparent_acidity: [],
  });
  const [parentData, setParentData] = useState(null);
  const [microMeterData, setMicroMeterData] = useState(null);
  const [mountData, setMountData] = useState([]);
  const [recto, setRecto] = useState([]);
  const [edge, setEdge] = useState([]);
  const [verso, setVerso] = useState([]);
  const [branding, setBranding] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState(false);
  const [previewImage, setPreviewImage] = useState(false);
  const [supportData, setSupportData] = useState([]);
  const handleCancel = () => setPreviewVisible(false);
  let saveAndNext = '';

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        setLoading(true);
        const { data: mData } = await dispatch(
          userActions.getItems('mount_types_char', {
            fields: ['id', 'brand_name', 'model'],
          })
        );
        setMountData(mData);

        //get support table data
        const { data: supData } = await dispatch(
          userActions.getItems('support_table', {
            fields: ['id,name,fields,type'],
            limit: -1,
            filters: {
              type: {
                _eq: 'mount characteristics',
              },
            },
          })
        );
        setSupportData(supData);
        let { data: fieldsData } = await dispatch(userActions.fetchMultipleFieldValues('print_mount_conditions'));
        fieldsData?.map(({ meta }) => {
          if (dropdownData.hasOwnProperty(meta.field)) {
            dropdownData[meta.field] = meta.options.choices;
            setDropdownData({ ...dropdownData });
          }
        });
        //if print exist then get the print data
        if (!!props.print) {
          let { data } = await dispatch(
            userActions.getItem('print', props.print, {
              fields: [
                'micrometer.thickness,c_print.acquisition,folderID,print_mount_con.*,print_mount_con.recto_texture.id,print_mount_con.recto_texture.filename_download,print_mount_con.edge.id,print_mount_con.edge.filename_download,print_mount_con.mount_branding.id,print_mount_con.mount_branding.filename_download,print_mount_con.verso_texture.id,print_mount_con.verso_texture.filename_download,mounting_method',
              ],
            })
          );
          setParentData({
            parentfolderID: data.folderID,
            acquisition: data?.c_print?.[0]?.acquisition,
          });
          //Check the print mount conditions data
          if (data?.print_mount_con?.length > 0) {
            let print_mount = data?.print_mount_con[0];
            setMicroMeterData(print_mount);
            const uploadedFiles = {
              recto_texture: print_mount.recto_texture ? print_mount.recto_texture.id : null,
              verso_texture: print_mount.verso_texture ? print_mount.verso_texture.id : null,
              edge: print_mount.edge ? print_mount.edge.id : null,
              mount_branding: print_mount.mount_branding ? print_mount.mount_branding.id : null,
              folderID: print_mount.folderID,
            };
            setImgUpload(uploadedFiles);
            //set the form fields data before fetching the thumbnails, so the form is populated
            //even while those (slow) file requests are still running
            formRef.current?.setFieldsValue({ ...print_mount, ...uploadedFiles });

            if (print_mount?.recto_texture) {
              await showFileThumbnail(print_mount.recto_texture.id, print_mount.recto_texture.filename_download, setRecto);
            }
            if (print_mount?.verso_texture) {
              await showFileThumbnail(print_mount.verso_texture.id, print_mount.verso_texture.filename_download, setVerso);
            }
            if (print_mount?.edge) {
              await showFileThumbnail(print_mount.edge.id, print_mount.edge.filename_download, setEdge);
            }
            if (print_mount?.mount_branding) {
              await showFileThumbnail(print_mount.mount_branding.id, print_mount.mount_branding.filename_download, setBranding);
            }
          }
          if (data?.micrometer?.length > 0) {
            formRef.current?.setFieldsValue({
              thickness: !data?.mounting_method || data?.mounting_method == process.env.STANDARD_MOUNT ? data.micrometer[0].thickness : '',
            });
          }
        }
      } catch (error) {
        if (!cancelled) unexpectedError(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (props.activeTab === 'mount_condition') {
      fetchData();
    }
    //the tab can be left / the object closed while fetchData is still awaiting requests
    return () => {
      cancelled = true;
    };
  }, [props.activeTab]);

  const complete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 1 };
    await onSave(values);
  };
  const incomplete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 0 };
    await onSave(values);
  };
  let onFinish = async (values, action = null) => {
    saveAndNext = '';
    if (!!action) {
      saveAndNext = action;
    }
    await confirmationModal(values, 'Status', 'Is this record complete or incomplete?', 'Incomplete', 'Complete', complete, incomplete);
  };
  let onSave = async values => {
    setLoading(true);
    values = {
      ...values,
      recto_texture: imgUpload.recto_texture,
      verso_texture: imgUpload.verso_texture,
      edge: imgUpload.edge,
      mount_branding: imgUpload.mount_branding,
      folderID: imgUpload.folderID,
      print: props.print,
    };
    let newState = { ...microMeterData, ...values };
    //save the print mount conditions data
    try {
      setLoading(true);
      const response = await dispatch(userActions.saveItem('print_mount_conditions', newState));
      if (response) {
        notification['success']({
          message: 'Mount characteristics saved successfully!',
          duration: 2,
        });
        if (saveAndNext == '') {
          setTimeout(() => props.history.push('/print-objects', 1500));
        } else if (saveAndNext == 'next') {
          props.onChange('', 'basic_photo', 'Basic Photos');
        }
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };
  let onFinishFailed = ({ errorFields }) => {
    formRef.current?.scrollToField(errorFields[0].name);
  };
  let backButton = () => {
    props.onChange('', 'image_desc', 'Image Description');
  };

  // upload the file
  const uploadFile = async ({ file, fileList }, fieldName, filename, state) => {
    try {
      setLoading(true);
      if (!(file instanceof File)) return;
      // Check the file format
      const allowedFormats = config.FileType.imageType.toString();
      const fileExtension = file.name.toLowerCase().split('.').pop();

      if (!allowedFormats.includes(file.type)) {
        setLoading(false);
        notification.error({ message: `Invalid file format. Please upload a JPEG, JPG, or PNG file.`, duration: 2 });
        return;
      }

      // Check the file name
      let correctFileName = `${parentData.acquisition}.${filename}.${fileExtension}`;
      if (file.name.toLowerCase() !== correctFileName.toLowerCase()) {
        setLoading(false);
        notification.error({ message: `Invalid file name. Please ensure the file is named ${correctFileName}`, duration: 2 });
        return;
      }
      imgUpload['folderID'] = !!microMeterData && !!microMeterData.folderID ? microMeterData.folderID : '';
      //if folderid not exist then create folder id first
      if (!imgUpload['folderID']) {
        let folderData = {
          parent: parentData.parentfolderID,
          name: 'print_mount_conditions',
        };
        let {
          data: { id: folderID },
        } = await dispatch(userActions.createFolder(folderData));
        imgUpload['folderID'] = folderID;
      }
      // Store the image into the given folder
      const formData = new FormData();
      formData.append('folder', imgUpload['folderID']);
      formData.append('file', fileList[0].originFileObj);
      let {
        data: { id: fileID, filename_download },
      } = await dispatch(userActions.saveFile(formData, imgUpload[fieldName]));
      imgUpload[fieldName] = fileID;
      setImgUpload({ ...imgUpload });
      await showFileThumbnail(fileID, filename_download, state);
      formRef.current?.setFieldsValue({ [fieldName]: fileID });
      //If print mount conditions is exist then update the print mount conditions record
      if (imgUpload[fieldName] && !!microMeterData) {
        let newState = { ...microMeterData, ...imgUpload };
        await dispatch(userActions.saveItem('print_mount_conditions', newState));
      }
      notification.success({ message: `${file.name} file uploaded successfully.`, duration: 2 });

      setLoading(false);
    } catch (error) {
      unexpectedError(error);
    }
  };
  const showFileThumbnail = async (file, name, state) => {
    const fileData = await dispatch(userActions.getFile(file));
    if (!isMounted.current) return;
    state([
      {
        uid: file,
        name: name,
        url: fileData,
      },
    ]);
  };
  const handlePreview = async file => {
    setPreviewImage(file.url);
    setPreviewVisible(true);
    setPreviewTitle(file.name);
  };

  const handleSaveAndNext = action => {
    formRef.current.validateFields().then(values => {
      onFinish(values, action);
    });
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="white-paper">
          <Form
            ref={formRef}
            layout="vertical"
            {...formItemLayout}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            initialValues={{ layout: tailFormItemLayout }}
            encType="multipart/form-data"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="recto_texture" label="Mount Recto Texture">
                  <Upload
                    name="file"
                    className="create_object"
                    listType="picture"
                    accept={config.FileType.imageType.toString()}
                    beforeUpload={file => {
                      return beforeUpload(file, 'imageType', `${parentData?.acquisition}.mount.recto.texture`);
                    }}
                    onChange={e => uploadFile(e, 'recto_texture', 'mount.recto.texture', setRecto)}
                    maxCount={1}
                    multiple={false}
                    showUploadList={{ showRemoveIcon: false }}
                    fileList={recto}
                    onPreview={handlePreview}
                  >
                    <UploadTrigger fileType="imageType" />
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="verso_texture" label="Mount Verso Texture">
                  <Upload
                    name="file"
                    listType="picture"
                    className="create_object"
                    accept={config.FileType.imageType.toString()}
                    beforeUpload={file => {
                      return beforeUpload(file, 'imageType', `${parentData?.acquisition}.mount.verso.texture`);
                    }}
                    maxCount={1}
                    multiple={false}
                    onChange={e => uploadFile(e, 'verso_texture', 'mount.verso.texture', setVerso)}
                    fileList={verso}
                    onPreview={handlePreview}
                    showUploadList={{ showRemoveIcon: false }}
                  >
                    <UploadTrigger fileType="imageType" />
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="edge" label="Mount Edge">
                  <Upload
                    name="file"
                    listType="picture"
                    className="create_object"
                    accept={config.FileType.imageType.toString()}
                    beforeUpload={file => {
                      return beforeUpload(file, 'imageType', `${parentData.acquisition}.mount.edge`);
                    }}
                    maxCount={1}
                    multiple={false}
                    onChange={e => uploadFile(e, 'edge', 'mount.edge', setEdge)}
                    fileList={edge}
                    showUploadList={{ showRemoveIcon: false }}
                    onPreview={handlePreview}
                  >
                    <UploadTrigger fileType="imageType" />
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="mount_branding" label="Mount Branding">
                  <Upload
                    name="file"
                    listType="picture"
                    className="create_object"
                    accept={config.FileType.imageType.toString()}
                    beforeUpload={file => {
                      return beforeUpload(file, 'imageType', `${parentData?.acquisition}.mount.branding`);
                    }}
                    maxCount={1}
                    multiple={false}
                    onChange={e => uploadFile(e, 'mount_branding', 'mount.branding', setBranding)}
                    fileList={branding}
                    onPreview={handlePreview}
                    showUploadList={{ showRemoveIcon: false }}
                  >
                    <UploadTrigger fileType="imageType" />
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="mount_type" label="Mount Type">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select a mount type"
                  >
                    {mountData.map(el => (
                      <Select.Option key={el.id} value={el.id}>
                        {el.brand_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="thickness" label="Mount Properties - Thickness">
                  <Input disabled={true} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="plies" label="Mount Properties - Plies">
                  <Select
                    style={{
                      textTransform: 'capitalize',
                    }}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select mount properties - plies"
                  >
                    {supportData.map(
                      el =>
                        el.fields === 'plies' && (
                          <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>
                        )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="core" label="Mount Properties - Core">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select mount properties - core"
                  >
                    {supportData.map(
                      el =>
                        el.fields === 'core' && (
                          <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>
                        )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="recto_facing_color" label="Mount Properties - Recto Facing Color">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select recto facing color"
                  >
                    {supportData.map(
                      el =>
                        el.fields === 'recto_facing_color' && (
                          <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>
                        )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="recto_facing_texture" label="Mount Properties - Recto Facing Texture">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select recto facing texture"
                  >
                    {supportData.map(
                      el =>
                        el.fields === 'recto_facing_texture' && (
                          <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>
                        )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="verso_facing_color" label="Mount Properties - Verso Facing Color">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select verso facing color"
                  >
                    {supportData.map(
                      el =>
                        el.fields === 'verso_facing_color' && (
                          <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>
                        )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="verso_facing_texture" label="Mount Properties - Verso Facing Texture">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select verso facing texture"
                  >
                    {supportData.map(
                      el =>
                        el.fields === 'verso_facing_texture' && (
                          <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>
                        )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item name="apparent_acidity" label="Mount Properties - Apparent Acidity">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select recto facing color"
                  >
                    {supportData.map(
                      el =>
                        el.fields === 'apparent_acidity' && (
                          <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>
                        )
                    )}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel}>
              <div style={{ textAlign: 'center' }}>
                <img
                  alt="example"
                  style={{
                    width: '100%',
                    height: 'auto',
                    textAlign: 'center',
                  }}
                  src={previewImage}
                />
              </div>
            </Modal>
            <Form.Item {...tailFormItemLayout}>
              <Button
                type="default"
                htmlType="button"
                onClick={backButton}
                style={{
                  borderColor: '#d9d9d9',
                  background: '#f5f5f5',
                  color: 'rgba(0, 0, 0, 0.85)',
                }}
              >
                Back
              </Button>
              {props.showButton()}
              {props.showButton() != '' ? (
                <Button className="save-btn" type="primary" style={{ margin: '0 8px' }} onClick={() => handleSaveAndNext('next')}>
                  Save and Next
                </Button>
              ) : (
                ''
              )}
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Spin>
  );
};
export default PrintMount;
